import { defineStore } from 'pinia';
import { isString, round } from 'lodash-es';
import { Notification } from 'element-ui';
import { useWindowSize } from '@vueuse/core';
import { whiteList } from '@/permission';

export const useAppStore = defineStore('appStore', () => {
  const sideBarCollapseWidth = 56;
  const sideBarMinWidth = 208;
  const sideBarMaxWidth = 480;

  const titleSplit = computed(() => useTitle().value?.split('-') || []);

  // 系统名称
  const ROSA_TITLE = ref(titleSplit.value[titleSplit.value.length - 1]);
  // 平台环境
  const ROSA_PLATFORM = ref(import.meta.env.ROSA_PLATFORM);
  // 系统版本
  const ROSA_VERSION = ref(import.meta.env.ROSA_VERSION);
  // 系统API前缀
  const ROSA_BASE_API = ref(import.meta.env.ROSA_BASE_API);
  // Minio文件下载地址
  const ROSA_MINIO_DOWNLOAD = ref(import.meta.env.ROSA_MINIO_DOWNLOAD);
  // Minio文件存储桶
  const ROSA_MINIO_BUCKET = ref(import.meta.env.ROSA_MINIO_BUCKET);
  // Minio图片访问地址
  const ROSA_MINIO_FILE = ref(import.meta.env.ROSA_MINIO_FILE);
  // ImgProxy图片访问地址
  const ROSA_IMG_PROXY = ref(import.meta.env.ROSA_IMG_PROXY);
  // Iconfont JSON
  const ROSA_ICONFONT_MENU = ref(import.meta.env.ROSA_ICONFONT_MENU);
  // 版权
  const ROSA_COPYRIGHT = ref(import.meta.env.ROSA_COPYRIGHT);
  // 是否开放电子签章
  const ROSA_HAS_E_SIGN = ref(import.meta.env.ROSA_HAS_E_SIGN);
  const hasESign = computed(() => ROSA_HAS_E_SIGN.value === 'TRUE');
  // 环境标记，可区分是哪一个项目
  const ROSA_ENV_TAG = ref(import.meta.env.ROSA_ENV_TAG);
  const ROSA_ENV = ref(import.meta.env.ROSA_ENV);

  const appVersion = useLocalStorage('appVersion', ROSA_VERSION.value);

  useFavicon().value = `/favicons/${ROSA_PLATFORM.value || 'favicon'}.ico`;

  const collapse = useSessionStorage('collapse', false);

  const toggleCollapse = () => {
    collapse.value = !collapse.value;
  };

  // 定义断点（参考 Bootstrap 标准）
  const breakpoints = useBreakpoints({
    lg: 2560,
    md: 1920,
    sm: 1440,
    xs: 1024,
    xxs: 576,
  });

  // 监听屏幕尺寸
  const isMobile = breakpoints.smaller('xxs');

  const { width } = useWindowSize();

  // 是否小于1512
  const isBelow1512 = computed(() => width.value <= 1512);

  // 动态响应屏幕变化
  watch(isMobile, (newVal) => {
    collapse.value = newVal; // 屏幕变小时自动折叠，变大时自动展开
  });

  const sideBarWidth = useSessionStorage('sideBarWidth', 208);

  const sideBarWidthCssVar = useCssVar('--side-bar-width');

  const setSideBarWidth = (width: number) => {
    if (width < sideBarMinWidth || width > sideBarMaxWidth) return;
    sideBarWidth.value = round(width);
  };

  const resetApp = () => {
    appVersion.value = ROSA_VERSION.value;
  };

  watch(
    [collapse, sideBarWidth],
    ([c, w]) => {
      if (c) {
        sideBarWidthCssVar.value = `${sideBarCollapseWidth}px`;
      } else {
        sideBarWidthCssVar.value = `${w}px`;
      }
    },
    { immediate: true },
  );

  const route = useRoute();
  const path = computed(() => route.path);
  const isInWhiteList = computed(() => whiteList.some((p) => path.value.includes(p)));

  watchOnce(
    [ROSA_VERSION, appVersion],
    ([rosa, app]) => {
      if (rosa !== app && !isInWhiteList.value) {
        Notification({
          title: '当前系统不是最新版本',
          message: '请退出并重新登录完成系统更新',
          position: 'bottom-right',
          type: 'warning',
        });
      }
    },
    { immediate: true },
  );

  const userStore = useUserStore();
  const { userInfo } = storeToRefs(userStore);
  const lockTimer = 20; // 20 minutes
  const { counter, reset, pause, resume } = useInterval(60000, { controls: true });
  const showLockScreen = useSessionStorage('locked', '', {
    serializer: {
      read: (v: string) => (v === 'true' || v === 'false' ? '' : v),
      write: (v: string) => (isString(v) ? v : ''),
    },
  });
  const lockTimeout = () => {
    if (counter.value >= lockTimer) return;
    if (counter.value) reset();
    else resume();
  };
  const lockReset = () => {
    showLockScreen.value = '';
    reset();
    resume();
  };
  watch([isInWhiteList, counter], ([flag, val]) => {
    console.log(flag, val);
    const username = userInfo.value.username || userInfo.value.userName;
    if (!flag && username && val >= lockTimer) {
      pause();
      showLockScreen.value = username;
    }
  });

  return {
    ROSA_TITLE,
    ROSA_PLATFORM,
    ROSA_VERSION,
    ROSA_BASE_API,
    ROSA_ENV_TAG,
    ROSA_MINIO_DOWNLOAD,
    ROSA_MINIO_BUCKET,
    ROSA_MINIO_FILE,
    ROSA_IMG_PROXY,
    ROSA_ICONFONT_MENU,
    ROSA_COPYRIGHT,
    ROSA_ENV,
    sideBarWidth,
    appVersion,
    collapse,
    showLockScreen,
    hasESign,
    lockTimeout,
    lockReset,
    resetApp,
    toggleCollapse,
    setSideBarWidth,
    isMobile,
    isBelow1512,
  };
});
